// i18n-Mandate (memory: i18n_mandate.md): UI-Texte gehen über
// diesen Helper, nicht hartkodiert ins Markup. Singleton-Store
// nach dem Muster von actions.svelte.ts und security.svelte.ts —
// Initial-Fetch beim Boot, danach in Memory gehalten und reaktiv
// über einen SvelteMap.
//
// API-Schema:
//   t(key, fallback?)  → übersetzt oder Fallback (sonst der Key
//                         selbst, damit fehlende Keys sofort
//                         sichtbar sind)
//   getLang()          → aktuelle Sprache (string)
//   setLang(lang)      → wechselt Sprache + cached neu laden
//
// Startup wird einmal in App.svelte über startI18n() angestoßen.
// Sprache persistiert seit dem prefs-Refactor ueber lib/prefs.svelte
// (Server-SSOT + localStorage-Cache + WS-Live-Sync); WS-Update von
// einem anderen Geraet wechselt die Sprache live im Tab.

import { SvelteMap } from "svelte/reactivity";
import { getPref, setPref, subscribePref } from "./prefs.svelte";

const DEFAULT_LANG = "de";
const KEY = "lang";
const LEGACY_KEY = "homelab.lang";

const _translations = new SvelteMap<string, string>();
const _lang = new SvelteMap<string, string>([["current", DEFAULT_LANG]]);

let _started = false;
let _legacyMigrated = false;

function migrateLegacy(): string | undefined {
  if (_legacyMigrated) return undefined;
  _legacyMigrated = true;
  if (typeof localStorage === "undefined") return undefined;
  if (getPref(KEY) !== undefined) return undefined;
  const v = localStorage.getItem(LEGACY_KEY);
  if (!v) return undefined;
  setPref(KEY, v);
  localStorage.removeItem(LEGACY_KEY);
  return v;
}

async function fetchTranslations(lang: string): Promise<void> {
  try {
    const r = await fetch(
      `/v2-api/i18n/translations?lang=${encodeURIComponent(lang)}`,
      { credentials: "include" },
    );
    if (!r.ok) return;
    const map = (await r.json()) as Record<string, string>;
    _translations.clear();
    for (const [k, v] of Object.entries(map)) {
      _translations.set(k, v);
    }
  } catch {
    // ignore — Komponenten fallen auf den per-Aufruf Default zurück
  }
}

export function startI18n(): void {
  if (_started) return;
  _started = true;
  // Sprache holen: erst migrieren (legacy localStorage), dann aus prefs.
  migrateLegacy();
  const lang = (getPref<string>(KEY) ?? DEFAULT_LANG) || DEFAULT_LANG;
  _lang.set("current", lang);
  void fetchTranslations(lang);

  // Live-Sync: wenn ein anderes Geraet die Sprache wechselt, ziehen
  // wir die Translations nach.
  subscribePref(KEY, (v) => {
    const next = typeof v === "string" && v ? v : DEFAULT_LANG;
    if (_lang.get("current") === next) return;
    _lang.set("current", next);
    void fetchTranslations(next);
  });
}

// t resolves a key to its translated value, falling back to the
// supplied default (typically the German source string for new
// code) and finally to the key itself. Calls are reactive — the
// underlying SvelteMap.get() tracks reads, so when the map is
// repopulated after a language switch, the surrounding component
// re-renders.
export function t(key: string, fallback?: string): string {
  const v = _translations.get(key);
  if (v !== undefined) return v;
  return fallback ?? key;
}

export function getLang(): string {
  return _lang.get("current") ?? DEFAULT_LANG;
}

export async function setLang(lang: string): Promise<void> {
  if (lang === getLang()) return;
  _lang.set("current", lang);
  setPref(KEY, lang);
  await fetchTranslations(lang);
}
